import React, {useState} from 'react';
import {
  Box,
  Menu,
  MenuItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Typography,
  CircularProgress,
  ButtonBase,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import {useNavigate} from 'react-router-dom';
import {useOrg} from '../contexts/OrgContext';

export default function OrgSwitcher() {
  const {activeOrg, organizations, loading, switchOrg} = useOrg();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  if (loading) {
    return <CircularProgress size={20} />;
  }

  if (organizations.length === 0) {
    return (
      <ButtonBase
        onClick={() => navigate('/org/new')}
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          borderRadius: '8px',
          border: '1px solid #E5E7EB',
          bgcolor: '#fff',
          '&:hover': {bgcolor: '#F9FAFB'},
        }}
      >
        <AddIcon sx={{fontSize: 18, color: '#6B7280'}} />
        <Typography sx={{fontSize: 13, fontWeight: 500, color: '#374151'}}>
          Create Org
        </Typography>
      </ButtonBase>
    );
  }

  return (
    <>
      <ButtonBase
        onClick={e => setAnchorEl(e.currentTarget)}
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          borderRadius: '8px',
          border: '1px solid #E5E7EB',
          bgcolor: '#fff',
          '&:hover': {bgcolor: '#F9FAFB', borderColor: '#D1D5DB'},
          transition: 'all 0.15s ease',
        }}
      >
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: '6px',
            bgcolor: '#EFF6FF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <BusinessIcon sx={{fontSize: 14, color: '#2563EB'}} />
        </Box>
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 600,
            color: '#111827',
            flex: 1,
            textAlign: 'left',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {activeOrg?.name || 'Select Org'}
        </Typography>
        <UnfoldMoreIcon sx={{fontSize: 16, color: '#9CA3AF', flexShrink: 0}} />
      </ButtonBase>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <Typography variant="caption" color="text.secondary" sx={{px: 2, py: 0.5, display: 'block'}}>
          Organizations
        </Typography>
        {organizations.map(org => (
          <MenuItem
            key={org.id}
            onClick={async () => {
              await switchOrg(org.id);
              setAnchorEl(null);
            }}
            selected={activeOrg?.id === org.id}
          >
            {activeOrg?.id === org.id && (
              <ListItemIcon>
                <CheckIcon fontSize="small" />
              </ListItemIcon>
            )}
            <ListItemText inset={activeOrg?.id !== org.id}>
              {org.name}
            </ListItemText>
          </MenuItem>
        ))}
        <Divider />
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            navigate('/org/new');
          }}
        >
          <ListItemIcon>
            <AddIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Create Organization</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}
